import { useTranslation } from 'react-i18next';
import { CALENDAR_ROLES, calendarPalette, legendKey, markShape, type CalendarRole } from '../../utils/calendarPalette';

/**
 * Легендата на календара.
 *
 * Строи се ОТ САМАТА ПАЛИТРА, не от ръчно изброени цветове. Дотогава легендата на
 * учителското табло показваше три записа (два брандови цвята и `bg-amber-500` като
 * Tailwind клас), докато решетката рисуваше пет различни цвята — тоест обещаваше
 * по-малко, отколкото показва, а amber-ът в нея дори не идваше от същата константа.
 *
 * Тук ролите са единственият източник: сменѝ палитрата и легендата я следва сама.
 */

export interface CalendarLegendProps {
  /** Брандовият primary — единственият цвят, който идва от училището. */
  primaryColor?: string | null;
  /**
   * Роли, които ТОЗИ календар не рисува. Легендата трябва да изброява точно каквото
   * решетката ползва: админското табло няма „като помощник", защото не различава
   * титуляр от помощник.
   */
  omit?: readonly CalendarRole[];
  /**
   * Записи, които НЕ идват от ролите — днес: по един на дете в родителския
   * календар.
   *
   * 🔴 Защо тук, а не втора легенда: две легенди под един календар се разминават
   * при първата промяна на палитрата, а родителят чете едната и вярва на другата.
   * Цветът на детето се избира от `childMarkColors`, тоест от същия източник.
   */
  extra?: ReadonlyArray<{ label: string; color: string; muted?: boolean }>;
  className?: string;
}

export default function CalendarLegend({ primaryColor, omit = [], extra = [], className = '' }: CalendarLegendProps) {
  const { t } = useTranslation();
  const palette = calendarPalette(primaryColor);
  const roles = CALENDAR_ROLES.filter((r) => !omit.includes(r));

  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 ${className}`}>
      {roles.map((role) => (
        <span key={role} className="inline-flex items-center gap-1.5">
          <span
            className={`w-2 h-2 flex-shrink-0 ${markShape(role) === 'square' ? 'rounded-[1px]' : 'rounded-full'}`}
            style={{ backgroundColor: palette[role] }}
          />
          {t(legendKey(role))}
        </span>
      ))}
      {extra.map((item) => (
        <span
          key={item.label}
          className={`inline-flex items-center gap-1.5 ${item.muted ? 'opacity-60' : ''}`}
        >
          {/* Маркерът на детето е ЧАС, тоест кръг — същото семейство като ролята. */}
          <span
            className="w-2 h-2 flex-shrink-0 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}
